import clsx from "clsx";
import { FC, useEffect, useMemo, useRef, useTransition } from "react";
import { FormProvider } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { PrivacyPDF, RASTER_LANDINGS, TermsPDF } from "@ryde/assets-landings";
import {
  CheckboxControlledMui,
  DropdownTemplate,
  MessagesDisplayContainer,
  PhoneTemplate,
  TextboxTemplate,
} from "@ryde/controls";
import { normalizePhoneNumber } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  LANDING_RYDE_SUPPORT_PHONE,
  landingColors,
  landingDropdownStationListAtom,
  landingInitDataAtom,
  landingMessagesAtom,
  NVIDIA_SHUTTLE_DESTINATION_STATIONS,
  NvidiaShuttleChooseStationStepFormFieldsEnum,
  useLandingProfile,
} from "@ryde/utils-landings";

import { IMarkerLocation, LandingAddressMap } from "../../layout/landing-map";
import { LandingAddressTemplate } from "../../layout/landing-templates";
import { ContinueButton } from "../../widgets";
import { useStepChooseStationNvidiaShuttle } from "./use-step-choose-station.nvidiashuttle";

export const StepChooseStationNvidiaShuttle: FC = () => {
  const {
    methods,
    handleChooseStation,
    handleConsentToggle,
    handleHomeAddressChange,
    isContinueCanBeApply,
  } = useStepChooseStationNvidiaShuttle();
  const { watch } = methods;

  const landingMessages = useRecoilValue(landingMessagesAtom);
  const stationsDropdownList = useRecoilValue(landingDropdownStationListAtom);
  const isHomeAddressTouchedRef = useRef<boolean>(false);
  const { fetchAndSetProfile } = useLandingProfile();
  const [, startHomeAddressChangeTransition] = useTransition();
  const [, startFetchProfileTransition] = useTransition();
  const isApiLoading = useRecoilValue(apiDataFetchingLandingAtom);
  const { t } = useTranslation();
  const { supportCenterPhone } = useRecoilValue(landingInitDataAtom) ?? {};

  const {
    latitude: lat,
    longitude: lng,
    fullAddress,
  } = watch(NvidiaShuttleChooseStationStepFormFieldsEnum.HomeAddress);
  const sourceStation2IdWatchField = watch(
    NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id,
  );
  const acceptTermsWatchField = watch(
    NvidiaShuttleChooseStationStepFormFieldsEnum.AcceptTerms,
  );
  const acceptPrivacyPolicyWatchField = watch(
    NvidiaShuttleChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
  );

  const markers = useMemo(() => {
    if (!lat || !lng) {
      return [];
    }

    const locations: IMarkerLocation[] = [
      {
        coordinate: { lat, lng },
        isCenter: true,
        popupText: fullAddress,
        icon: <SVG_MUI.HomePinSVG color={landingColors.purpleHaze} />,
      },
    ];

    const { locationInfo: pickUpLocation, label } =
      stationsDropdownList?.find(
        item => item.value === sourceStation2IdWatchField,
      ) ?? {};

    if (pickUpLocation?.latitude && pickUpLocation?.longitude) {
      locations.push({
        coordinate: {
          lat: pickUpLocation.latitude,
          lng: pickUpLocation.longitude,
        },
        isCenter: false,
        popupText: label ?? pickUpLocation.fullAddress,
      });
    }

    return locations;
  }, [fullAddress, lat, lng, sourceStation2IdWatchField, stationsDropdownList]);

  useEffect(() => {
    if ((lat || lng || fullAddress) && !isHomeAddressTouchedRef.current) {
      isHomeAddressTouchedRef.current = true;
    }

    if (isHomeAddressTouchedRef.current) {
      startHomeAddressChangeTransition(() => {
        handleHomeAddressChange();
      });
    }
  }, [lat, lng, fullAddress, handleHomeAddressChange]);

  useEffect(() => {
    startFetchProfileTransition(() => {
      fetchAndSetProfile();
    });
  }, [fetchAndSetProfile]);

  return (
    <FormProvider {...methods}>
      <div className="landing-top-illustration">
        <img src={RASTER_LANDINGS.CarFestImagePNG} alt="Car fest" />
      </div>
      <div className="landing-title landing-title_choose-station">
        {t("landing.chooseStation.titleNvidiaShuttle")}
      </div>
      <div className="landing-sub-title">
        {t("landing.chooseStation.subTitleNvidiaShuttle")}
      </div>

      <div className="landing-form-field">
        <label
          htmlFor={NvidiaShuttleChooseStationStepFormFieldsEnum.EmployeeNumber}
        >
          {t("landing.formLabel.employeeNumber")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={
            NvidiaShuttleChooseStationStepFormFieldsEnum.EmployeeNumber
          }
          name={NvidiaShuttleChooseStationStepFormFieldsEnum.EmployeeNumber}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={NvidiaShuttleChooseStationStepFormFieldsEnum.FirstName}>
          {t("landing.formLabel.firstName")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={NvidiaShuttleChooseStationStepFormFieldsEnum.FirstName}
          name={NvidiaShuttleChooseStationStepFormFieldsEnum.FirstName}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={NvidiaShuttleChooseStationStepFormFieldsEnum.LastName}>
          {t("landing.formLabel.lastName")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={NvidiaShuttleChooseStationStepFormFieldsEnum.LastName}
          name={NvidiaShuttleChooseStationStepFormFieldsEnum.LastName}
        />
      </div>

      <div className="landing-form-field">
        <label
          htmlFor={NvidiaShuttleChooseStationStepFormFieldsEnum.PhoneMobile}
        >
          {t("landing.formLabel.phone")}*
        </label>
        <PhoneTemplate
          uniqueAttrId={
            NvidiaShuttleChooseStationStepFormFieldsEnum.PhoneMobile
          }
          name={NvidiaShuttleChooseStationStepFormFieldsEnum.PhoneMobile}
          sx={{ size: "small" }}
          formAttributes={{ disablePhoneCallable: true }}
        />
      </div>

      <div className="landing-form-field">
        <label
          htmlFor={NvidiaShuttleChooseStationStepFormFieldsEnum.HomeAddress}
        >
          {t("landing.formLabel.homeAddress")}*
        </label>
        <LandingAddressTemplate
          uniqueAttrId={
            NvidiaShuttleChooseStationStepFormFieldsEnum.HomeAddress
          }
          name={NvidiaShuttleChooseStationStepFormFieldsEnum.HomeAddress}
          formAttributes={{
            isCoordinate: true,
          }}
        />
      </div>

      <div
        className={clsx("landing-form-field", {
          hidden: !stationsDropdownList?.length || !fullAddress,
        })}
      >
        <label
          htmlFor={
            NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id
          }
        >
          {t("landing.formLabel.sourceStation2Id")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={
            NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id
          }
          name={NvidiaShuttleChooseStationStepFormFieldsEnum.SourceStation2Id}
          size="medium"
          formAttributes={{
            selectItems: stationsDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <label
          htmlFor={NvidiaShuttleChooseStationStepFormFieldsEnum.DestinationId}
        >
          {t("landing.formLabel.destinationStation")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={
            NvidiaShuttleChooseStationStepFormFieldsEnum.DestinationId
          }
          name={NvidiaShuttleChooseStationStepFormFieldsEnum.DestinationId}
          size="medium"
          formAttributes={{
            selectItems: NVIDIA_SHUTTLE_DESTINATION_STATIONS,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <div className="landing-address-map-field">
          <LandingAddressMap locations={markers} readonly />
        </div>
      </div>

      <div className="landing-form-field">
        <div className="landing-accept-terms">
          <CheckboxControlledMui
            width={16}
            height={16}
            checked={acceptTermsWatchField ?? false}
            setCheckedToggle={() =>
              handleConsentToggle(
                NvidiaShuttleChooseStationStepFormFieldsEnum.AcceptTerms,
              )
            }
          />
          <span className="custom-label">
            {t("landing.formLabel.termsAndConditions")}{" "}
            <a
              href={TermsPDF}
              target="_blank"
              rel="noreferrer"
              aria-label={t("landing.formLabel.termsAndConditions")}
            >
              כאן
            </a>
          </span>
        </div>
      </div>

      <div className="landing-form-field">
        <div className="landing-accept-terms">
          <CheckboxControlledMui
            width={16}
            height={16}
            checked={acceptPrivacyPolicyWatchField ?? false}
            setCheckedToggle={() =>
              handleConsentToggle(
                NvidiaShuttleChooseStationStepFormFieldsEnum.AcceptPrivacyPolicy,
              )
            }
          />
          <span className="custom-label">
            {t("landing.formLabel.acceptPrivacyPolicy")}{" "}
            <a
              href={PrivacyPDF}
              target="_blank"
              rel="noreferrer"
              aria-label={t("landing.formLabel.acceptPrivacyPolicy")}
            >
              כאן
            </a>
          </span>
        </div>
      </div>

      <MessagesDisplayContainer
        serverMessages={landingMessages?.messages}
        type={landingMessages?.type}
      />

      <ContinueButton
        disabled={!isContinueCanBeApply}
        onClick={handleChooseStation}
        loading={isApiLoading}
      />

      <div className="landing-container-footer">
        <div className="landing-container-footer-logo">
          <img
            className="landing-logo__img"
            src={RASTER_LANDINGS.RideLogoPNG}
            alt="Ryde logo"
            width="120"
            height="51"
          />
        </div>

        <span className="landing-customer-support custom-label">
          <Trans
            i18nKey="landing.helpDisclaimer"
            values={{
              phoneNumber: normalizePhoneNumber(LANDING_RYDE_SUPPORT_PHONE),
            }}
            components={{
              secure_link: (
                <a
                  aria-label="call center phone number"
                  href={`tel:${LANDING_RYDE_SUPPORT_PHONE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              br: <br />,
            }}
          />
        </span>
      </div>
    </FormProvider>
  );
};
